import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Send, PlayCircle, StopCircle, FileCheck } from 'lucide-react';
import { useAlert } from '../context/AlertContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWWorkflowActions = ({ permit, onUpdate }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [error, setError] = useState(null);

  const handleAction = async (action, payload = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      switch (action) {
        case 'submit':
          response = await axios.post(
            `${API}/permits/${permit.id}/submit-for-approval`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'approve':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve`,
            { approval_status: 'Approved', comments: payload.comments || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'reject':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve`,
            { approval_status: 'Rejected', comments: payload.comments || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'issue':
          response = await axios.post(
            `${API}/permits/${permit.id}/issue`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'close':
          response = await axios.post(
            `${API}/permits/${permit.id}/close`,
            { closure_remarks: payload.remarks || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'suspend':
          response = await axios.post(
            `${API}/permits/${permit.id}/suspend`,
            { reason: payload.reason || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'extend':
          response = await axios.post(
            `${API}/permits/${permit.id}/request-extension`,
            {
              extension_end_date: payload.extension_end_date,
              extension_reason: payload.extension_reason
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'approve-extension':
          response = await axios.post(
            `${API}/permits/${permit.id}/approve-extension`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
          
        case 'hse-verify':
          response = await axios.post(
            `${API}/permits/${permit.id}/hse-verify`,
            { hse_verification_remarks: payload.remarks || '' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          break;
      }
      
      setShowModal(null);
      setModalData({});
      setLoading(false);
      
      if (onUpdate) {
        onUpdate();
      }

      showSuccess(response.data.message || 'Action completed successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Action failed');
      setLoading(false);
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    const modalConfigs = {
      approve: {
        title: 'Approve Permit',
        icon: <CheckCircle className="w-6 h-6 text-green-600" />,
        color: 'green',
        fields: [
          { name: 'comments', label: 'Approval Comments', type: 'textarea', placeholder: 'Add approval comments (optional)' }
        ]
      },
      reject: {
        title: 'Reject Permit',
        icon: <XCircle className="w-6 h-6 text-red-600" />,
        color: 'red',
        fields: [
          { name: 'comments', label: 'Rejection Reason', type: 'textarea', placeholder: 'Reason for rejection (required)', required: true }
        ]
      },
      close: {
        title: 'Close Permit',
        icon: <FileCheck className="w-6 h-6 text-purple-600" />,
        color: 'purple',
        fields: [
          { name: 'remarks', label: 'Closure Remarks', type: 'textarea', placeholder: 'Work completion remarks' }
        ]
      },
      suspend: {
        title: 'Suspend Permit',
        icon: <StopCircle className="w-6 h-6 text-red-600" />,
        color: 'red',
        fields: [
          { name: 'reason', label: 'Suspension Reason', type: 'textarea', placeholder: 'Emergency suspension reason (required)', required: true }
        ]
      },
      extend: {
        title: 'Request Extension',
        icon: <PlayCircle className="w-6 h-6 text-orange-600" />,
        color: 'orange',
        fields: [
          { name: 'extension_end_date', label: 'New End Date', type: 'datetime-local', required: true },
          { name: 'extension_reason', label: 'Extension Reason', type: 'textarea', placeholder: 'Reason for extension (required)', required: true }
        ]
      },
      'hse-verify': {
        title: 'HSE Verification',
        icon: <CheckCircle className="w-6 h-6 text-green-600" />,
        color: 'green',
        fields: [
          { name: 'remarks', label: 'Verification Remarks', type: 'textarea', placeholder: 'HSE verification remarks' }
        ]
      }
    };

    const config = modalConfigs[showModal];
    if (!config) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {config.icon}
              <h3 className="text-xl font-bold text-gray-900">{config.title}</h3>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              {config.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={modalData[field.name] || ''}
                      onChange={(e) => setModalData({ ...modalData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      rows="4"
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={modalData[field.name] || ''}
                      onChange={(e) => setModalData({ ...modalData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowModal(null);
                  setModalData({});
                  setError(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleAction(showModal, modalData)}
                className={`flex-1 bg-${config.color}-600 hover:bg-${config.color}-700`}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActions = () => {
    const actions = [];
    const status = permit.status;
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // Draft status
    if (status === 'Draft') {
      actions.push(
        <Button
          key="submit"
          onClick={() => handleAction('submit')}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit for Approval
        </Button>
      );
    }

    // Pending Approval status - Check if user is the assigned approver
    if (status === 'Pending Approval') {
      const currentApprovalLevel = permit.current_approval_level || 0;
      const approvals = permit.approvals || [];
      const currentApprover = approvals[currentApprovalLevel];
      const userId = user.user_id || user.id;
      
      // Show approve/reject if user is the current approver or admin/manager
      const canApprove = (currentApprover && currentApprover.approver_id === userId) || 
                        user.role === 'Admin' || 
                        user.role === 'admin' || 
                        user.role === 'HSE Manager' || 
                        user.role === 'hse_manager' ||
                        user.role === 'Safety Manager';
      
      if (canApprove) {
        actions.push(
          <Button
            key="approve"
            onClick={() => setShowModal('approve')}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve
          </Button>,
          <Button
            key="reject"
            onClick={() => setShowModal('reject')}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </Button>
        );
      }
    }

    // Approved status
    const canIssue = user.role === 'Admin' || user.role === 'admin' || user.role === 'HSE Manager' || user.role === 'hse_manager' || user.role === 'Safety Manager';
    if (status === 'Approved' && canIssue) {
      actions.push(
        <Button
          key="issue"
          onClick={() => handleAction('issue')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />}
          Issue & Activate
        </Button>
      );
    }

    // Active status
    if (status === 'Active') {
      actions.push(
        <Button
          key="extend"
          onClick={() => setShowModal('extend')}
          disabled={loading}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <PlayCircle className="w-4 h-4 mr-2" />
          Request Extension
        </Button>,
        <Button
          key="close"
          onClick={() => setShowModal('close')}
          disabled={loading}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <FileCheck className="w-4 h-4 mr-2" />
          Close Permit
        </Button>
      );
      
      const canSuspend = user.role === 'Admin' || user.role === 'admin' || user.role === 'HSE Manager' || user.role === 'hse_manager' || user.role === 'Safety Manager';
      if (canSuspend) {
        actions.push(
          <Button
            key="suspend"
            onClick={() => setShowModal('suspend')}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            <StopCircle className="w-4 h-4 mr-2" />
            Suspend
          </Button>
        );
      }
    }

    // Extension Requested status
    const canApproveExtension = user.role === 'Admin' || user.role === 'admin' || user.role === 'HSE Manager' || user.role === 'hse_manager' || user.role === 'Safety Manager';
    if (status === 'Extension Requested' && canApproveExtension) {
      actions.push(
        <Button
          key="approve-extension"
          onClick={() => handleAction('approve-extension')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Approve Extension
        </Button>
      );
    }

    // Closed status
    if (status === 'Closed' && (user.role === 'admin' || user.role === 'Admin' || user.role === 'hse_manager' || user.role === 'HSE Manager')) {
      actions.push(
        <Button
          key="hse-verify"
          onClick={() => setShowModal('hse-verify')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          HSE Verify
        </Button>
      );
    }

    return actions;
  };

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {renderActions()}
      </div>
      {renderModal()}
    </>
  );
};

export default PTWWorkflowActions;
